// Global Instructions Rule Applied!
//
// Fund selection + persistence for the prospects mobile list — the primary selector that
// replaces the old pipeline switcher. The user picks a FUND (the funds that have prospects);
// that (a) filters the list to the fund and (b) resolves the fund's Fundraising pipeline so
// the Stage filter + stage colours follow it. Fund options come from the existing prospects
// filter-options endpoint (key `prospects__fund` = distinct funds present in prospects), so
// no new backend read. The pick persists under `data.mobile.prospect_fund_id` of the EXISTING
// `fundraising` menu-item preference row via read-merge-write (the same row the desktop table
// stores its filters in — the PATCH replaces `data` wholesale, so we spread it forward and
// only add our sub-key, leaving desktop's filters untouched).

import { useCallback, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { store } from '../../../../state_manager/store.ts'
import { MENU_ITEM_KEY_FUNDRAISING } from '../../../../state_manager/globals.ts'
import { useMenuItemUserPreference } from '../../../../app_modules/global_configs/useMenuItemUserPreference.ts'
import { mergeUserPreferenceRowData } from '../../../../state_manager/state_update_actions/global_configs/actions.ts'
import { fetchGlobalConfigsThunk } from '../../../../app_modules/global_configs/controller.ts'
import { fetchFundraisingFilterOptions } from '../../../../app_modules/fundraising/services/fundraisingService.ts'
import { getAccessToken } from '../../../auth/authService'
import { resolveFundraisingPipelineId } from './pipelineOptions.ts'
import { ModuleListDescriptor } from './listDescriptors.ts'

const MOBILE_DATA_KEY = 'mobile'
const PROSPECT_FUND_KEY = 'prospect_fund_id'
/** Wire filter key the prospects read matches against `pr.fund` (also the filter-options key). */
export const PROSPECT_FUND_FILTER_KEY = 'prospects__fund'

export interface FundOption {
  id: string
  name: string
}

export interface ListFundState {
  /** Funds that have prospects (selector options); empty for non fund-scoped modules. */
  fundOptions: FundOption[]
  /** Selected fund id, or null when not fund-scoped / not resolved yet. */
  selectedFundId: string | null
  /** The selected fund's Fundraising pipeline id — drives the Stage filter options. */
  resolvedPipelineId: string | null
  /** Commit + persist a new fund pick. */
  onFundChange: (fundId: string) => void
}

/**
 * Resolve + persist the selected fund for a fund-scoped list descriptor (prospects). A no-op
 * (empty options, null selection) for descriptors without a `pipelineType`.
 */
export function useListFund(descriptor: ModuleListDescriptor | null): ListFundState {
  const dispatch = useDispatch()
  const isFundScoped = Boolean(descriptor?.pipelineType) // prospects (Fundraising) only
  const { menuItemId, preferenceRow, persistPreference } = useMenuItemUserPreference(MENU_ITEM_KEY_FUNDRAISING)
  const globalConfigs = useSelector((s: any) => s?.globalConfigs)

  const [fundOptions, setFundOptions] = useState<FundOption[]>([])
  const [selectedFundId, setSelectedFundId] = useState<string | null>(null)
  const [optionsLoaded, setOptionsLoaded] = useState(false)

  // Reset when the module changes — ListScreen is one instance reused across /browse/:module.
  const [trackedModule, setTrackedModule] = useState<string | undefined>(descriptor?.module)
  if (descriptor?.module !== trackedModule) {
    setTrackedModule(descriptor?.module)
    setSelectedFundId(null)
    setFundOptions([])
    setOptionsLoaded(false)
  }

  // Fetch the fund options once per mount of a fund-scoped module.
  useEffect(() => {
    if (!isFundScoped) {
      setOptionsLoaded(true)
      return
    }
    let cancelled = false
    const token = getAccessToken(store.getState().auth)
    void fetchFundraisingFilterOptions(token, [PROSPECT_FUND_FILTER_KEY])
      .then((res) => {
        if (cancelled) return
        const raw = (res.data?.[PROSPECT_FUND_FILTER_KEY] ?? []) as Array<{ value: unknown; label: unknown }>
        const opts = raw
          .map((o) => ({ id: String(o.value), name: String(o.label ?? '') }))
          .filter((o) => o.id && o.name)
        setFundOptions(opts)
        setOptionsLoaded(true)
      })
      .catch(() => {
        if (!cancelled) setOptionsLoaded(true)
      })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isFundScoped, descriptor?.module])

  // Resolve the initial selected fund once options load (persisted-if-valid, else the first).
  if (isFundScoped && optionsLoaded && selectedFundId === null && fundOptions.length > 0) {
    const persisted = (preferenceRow?.data as any)?.[MOBILE_DATA_KEY]?.[PROSPECT_FUND_KEY]
    const valid = typeof persisted === 'string' && fundOptions.some((f) => f.id === persisted)
    setSelectedFundId(valid ? persisted : fundOptions[0].id)
  }

  const onFundChange = useCallback(
    (fundId: string) => {
      setSelectedFundId(fundId)
      if (!menuItemId) return
      // Read-merge-write the EXISTING fundraising preference row so the desktop table's
      // `table_filters`/`sorting` (also in `data`) survive the wholesale-replace PATCH.
      const reports = store.getState().globalConfigs?.user_preferences_reports ?? []
      const hadExistingRow = reports.some((p: any) => p.menu_item_id === menuItemId)
      const row = reports.find((p: any) => p.menu_item_id === menuItemId)
      const existingData = (row?.data ?? {}) as Record<string, unknown>
      const existingMobile = (existingData[MOBILE_DATA_KEY] ?? {}) as Record<string, unknown>
      const merged: Record<string, unknown> = {
        ...existingData,
        [MOBILE_DATA_KEY]: { ...existingMobile, [PROSPECT_FUND_KEY]: fundId }
      }
      void persistPreference(undefined, merged).then((result) => {
        if (!result.success) {
          if (process.env.NODE_ENV === 'development') {
            console.error('Failed to persist mobile prospect fund:', result.error)
          }
          return
        }
        if (hadExistingRow) {
          dispatch(mergeUserPreferenceRowData(menuItemId, merged))
        } else {
          const user = store.getState().auth?.user
          if (user) void fetchGlobalConfigsThunk(user)(dispatch)
        }
      })
    },
    [menuItemId, persistPreference, dispatch]
  )

  const resolvedPipelineId = isFundScoped ? resolveFundraisingPipelineId(globalConfigs, selectedFundId) : null

  return {
    fundOptions: isFundScoped ? fundOptions : [],
    selectedFundId: isFundScoped ? selectedFundId : null,
    resolvedPipelineId,
    onFundChange
  }
}
